import { useBrushes } from "hooks/BrushesProvider";
import { Flex } from "components/Flex";
import { Text } from "components/Text";
import styled, { css, useTheme } from "styled-components";
import { FaPen, FaPlusCircle, FaTimes, FaTrash } from "react-icons/fa";
import Modal from "components/Modal";
import React, { useCallback, useEffect, useState } from "react";
import { getUnixTime } from "date-fns";
import { v4 as uuid } from "uuid";
import { Controller, useForm } from "react-hook-form";
import { FormInput } from "components/FormInput";
import { Box } from "components/Box";
import { Button } from "components/Button";
import { PanelHeader } from "components/Panel";
import { TagInput } from "components/TagInput";
import { compose } from "lodash/fp";
import { desaturate, tint, shade, getLuminance } from "polished";
import { IconButton } from "components/IconButton";
import { Swatch } from "@aika/types";

const dim = compose(tint(0.3), desaturate(0.1));

export type BrushSelectProps = {
  value: string;
  onChange: (newId: string) => void;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const Rule = styled.hr`
  width: 100%;
  display: block;
  margin: 18px 0;
  background-color: ${(props) => props.theme.colors.body};
  border: 0;
  height: 1px;
  opacity: 0.2;
`;

const ColorSwatch = styled.button<{ colorIndex: number; active: boolean }>`
  background-color: ${(props) => props.theme.blockColors[props.colorIndex]};
  height: 22px;
  width: 22px;
  border: 2px solid ${(props) => props.theme.blockColors[props.colorIndex]};
  ${(props) => {
    if (!props.active) {
      return "";
    }

    const c1 = props.theme.blockColors[props.colorIndex];
    const c2 = dim(c1);
    const c = shade(0.3, props.theme.blockColors[props.colorIndex]);

    return css`
      border-color: ${c};
      background-image: linear-gradient(
        45deg,
        ${c1} 25%,
        ${c2} 25%,
        ${c2} 50%,
        ${c1} 50%,
        ${c1} 75%,
        ${c2} 75%,
        ${c2} 100%
      );
      background-size: 10px 10px;
    `;
  }};
`;

const AddSwatchButton = styled.button`
  background-color: ${(props) => props.theme.colors.grey1};
  border: 1.5px dashed ${(props) => props.theme.colors.grey3};
  margin-bottom: 18px;
  border-radius: 4px;
  font-size: ${(props) => props.theme.fontSizes[2]}px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.grey4};
  padding: 3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  & svg {
    margin-right: 6px;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.grey0};
  }
`;

const SwatchDisplay = styled.button<{ colorIndex: number; active: boolean }>`
  background-color: ${(props) => props.theme.blockColors[props.colorIndex]};
  min-height: 20px;
  margin-bottom: 3px;
  padding: 6px;
  border-radius: 3px;
  position: relative;
  flex: 0 0 auto;
  border: 0;
  border: 2px solid ${(props) => props.theme.blockColors[props.colorIndex]};

  ${(props) => {
    if (!props.active) {
      return "";
    }

    const c1 = props.theme.blockColors[props.colorIndex];
    const c2 = dim(c1);
    const c = shade(0.3, props.theme.blockColors[props.colorIndex]);

    return css`
      border-color: ${c};
      background-image: linear-gradient(
        45deg,
        ${c1} 25%,
        ${c2} 25%,
        ${c2} 50%,
        ${c1} 50%,
        ${c1} 75%,
        ${c2} 75%,
        ${c2} 100%
      );
      background-size: 56.57px 56.57px;
    `;
  }};
`;

const SwatchDescription = styled(Text)`
  text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.6);
  color: white;
`;

type EditSwatchModalProps = {
  value: Swatch;
  onSave: (values: Swatch) => void;
  onCancel: () => void;
  onDelete: () => void;
};

const LinkButton = styled.button`
  background: none !important;
  border: none;
  padding: 0 !important;
  color: ${(props) => props.theme.colors.primary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  &:hover {
    text-decoration: underline;
  }
`;

const Tag = styled.span`
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.body};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 3px;
  font-size: 12px;
  margin-right: 3px;
  margin-bottom: 3px;
`;

const EditButton = styled.button`
  position: absolute;
  top: 3px;
  right: 3px;
  font-size: 13px;
  padding: 0;
  color: white;
  opacity: 0.5;
  cursor: pointer;
  background: none;
  border: 0;
  display: none;

  &:hover {
    opacity: 1;
  }
  ${SwatchDisplay}:hover & {
    display: flex;
  }
`;

function EditSwatchModal({
  value,
  onSave,
  onCancel,
  onDelete,
}: EditSwatchModalProps) {
  const theme = useTheme();
  const { handleSubmit, reset, register, control } = useForm<Swatch>();

  useEffect(() => {
    reset(value);
  }, [reset, value]);

  const onSubmit = (value: Swatch) => {
    if (value.tags.length === 0 && value.description.trim() === "") {
      return window.alert("Need a description or at least one tag");
    }
    onSave(value);
  };

  return (
    <Modal onClose={onCancel} width="300px" p={0}>
      <PanelHeader>
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize={3} fontWeight={600}>
            Swatch
          </Text>
          <LinkButton onClick={onCancel}>
            <FaTimes />
          </LinkButton>
        </Flex>
      </PanelHeader>
      <Box p={3}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input type="hidden" {...register("id")} />
          <FormInput
            ref={register}
            name="description"
            label="Description"
            autoComplete="off"
            autoFocus={true}
          />
          <Controller
            name="tags"
            defaultValue={[]}
            control={control}
            render={({ value, onChange }) => (
              <TagInput value={value} onChange={onChange} />
            )}
          />
          <Text fontSize={2} fontWeight={600} mb={1}>
            Color
          </Text>
          <Controller
            name="color"
            control={control}
            render={({ value, onChange }) => (
              <Flex mb={3} flexWrap="wrap" width={180}>
                {theme.blockColors.map((_x, i) => (
                  <ColorSwatch
                    key={i}
                    colorIndex={i}
                    type="button"
                    onClick={() => onChange(i)}
                    active={value === i}
                  />
                ))}
              </Flex>
            )}
          />
          <Flex justifyContent="space-between" alignItems="flex-end">
            <Button type="submit">Save</Button>
            <IconButton icon={FaTrash} onClick={onDelete} />
          </Flex>
        </form>
      </Box>
    </Modal>
  );
}

export const BrushSelect = React.memo(
  ({ value, onChange }: BrushSelectProps) => {
    const { brushes, saveSwatch, swatches, removeSwatch } = useBrushes();
    const [editing, setEditing] = useState<Swatch | null>(null);
    const theme = useTheme();

    const newSwatch = useCallback(() => {
      setEditing({
        id: uuid(),
        tags: [],
        color: 0,
        description: "",
        createdOn: getUnixTime(new Date()),
      });
    }, []);

    const deleteEditingSwatch = useCallback(() => {
      const { id } = editing;
      setEditing(null);
      removeSwatch(id);
    }, [editing, removeSwatch]);

    return (
      <Container>
        {editing != null && (
          <EditSwatchModal
            value={editing}
            onSave={(v) => {
              saveSwatch(v);
              setEditing(null);
            }}
            onDelete={deleteEditingSwatch}
            onCancel={() => setEditing(null)}
          />
        )}
        <Flex flexWrap="wrap" justifyContent="center">
          {brushes
            .filter((x) => !x.isSwatch)
            .map((x, i) => (
              <ColorSwatch
                colorIndex={x.color}
                active={x.id === value}
                onClick={() => onChange(x.id)}
              />
            ))}
        </Flex>
        <Rule />
        <Text textAlign="center" fontWeight={600} fontSize={2} mb={2}>
          Swatches
        </Text>
        <AddSwatchButton onClick={newSwatch}>
          <FaPlusCircle />
          New Swatch
        </AddSwatchButton>
        <Flex overflowY="auto" flexDirection="column">
          {brushes
            .filter((x) => x.isSwatch)
            .map((x, i) => {
              const lum = getLuminance(theme.blockColors[x.color]);
              const readable = lum > 0.65 ? "#666" : "white";
              const shadowColor =
                lum > 0.65 ? "rgba(255,255,255,0.0)" : "rgba(0,0,0,0.6)";
              return (
                <SwatchDisplay
                  colorIndex={x.color}
                  active={x.id === value}
                  onClick={() => onChange(x.id)}
                >
                  <EditButton
                    onClick={() =>
                      setEditing(swatches.find((s) => s.id === x.id))
                    }
                    style={{ color: readable }}
                  >
                    <FaPen />
                  </EditButton>
                  {x.description && (
                    <SwatchDescription
                      mb={x.tags.length > 0 ? "3px" : 0}
                      fontSize={2}
                      fontWeight={600}
                      style={{
                        color: readable,
                        textShadow: `0px 1px 2px ${shadowColor} `,
                      }}
                    >
                      {x.description}
                    </SwatchDescription>
                  )}
                  {x.tags.length > 0 && (
                    <Flex flexWrap="wrap" justifyContent="center">
                      {x.tags.map((x) => (
                        <Tag>#{x}</Tag>
                      ))}
                    </Flex>
                  )}
                </SwatchDisplay>
              );
            })}
        </Flex>
      </Container>
    );
  }
);
